import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import GiftOutlineIcon from 'mdi-react/GiftOutlineIcon';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import CashIcon from 'mdi-react/CashIcon';
import renderField from '../../../shared/components/custom/Field';
import renderSelectField from '../../../shared/components/form/Select';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';
import InfoBox from '../../../shared/components/custom/InfoBox';
import { partial } from '../../../util/translation/translation';
import { formatDateTime } from '../../../util/date-and-time';
import useConfig from '../../../util/useConfig';
import {
  EXTERNAL_METHODS,
  bookingTotal,
  canExpireAtRegistrationEnd,
  eventCurrency,
  expiresAtFor,
  formatMoney,
  krInputToOre,
  oreToKrInput,
} from '../payments';

const MODE_ICONS = {
  free: GiftOutlineIcon,
  link: LinkVariantIcon,
  external: CashIcon,
};

// Quick picks for the payment link's deadline, as stats-range buttons
const ExpiryField = ({ input, event }) => {
  const p = partial('EventPayments');
  const choices = [
    '24h',
    '3d',
    ...(canExpireAtRegistrationEnd(event) ? ['registrationEnd'] : []),
    'custom',
  ];
  const labels = {
    '24h': p('expiry24h'),
    '3d': p('expiry3d'),
    registrationEnd: p('expiryRegistrationEnd'),
    custom: p('expiryCustom'),
  };
  return (
    <div className="event-payments__picks" role="radiogroup">
      {choices.map((choice) => (
        <button
          key={choice}
          type="button"
          role="radio"
          aria-checked={input.value === choice}
          className={`stats-range${input.value === choice ? ' active' : ''}`}
          onClick={() => input.onChange(choice)}
        >
          {labels[choice]}
        </button>
      ))}
    </div>
  );
};

ExpiryField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.string,
    onChange: PropTypes.func,
  }).isRequired,
  event: PropTypes.shape({}),
};

ExpiryField.defaultProps = {
  event: null,
};

/**
 * How someone added by an admin to a paid event pays: free of charge (a
 * comp), by a payment link sent by email, or outside the app (cash, Swish,
 * invoice). Fields named payment* / external* in the card form; the page
 * turns them into the add-participant body's payment (payments.js).
 * places: the person and their guests; email: where a link would go;
 * inApp: an app user, who gets the link without an email given here.
 */
const ParticipantPaymentFields = ({
  formValues,
  change,
  event,
  places,
  email,
  inApp,
}) => {
  const p = partial('EventPayments');
  const config = useConfig();
  const currency = eventCurrency(event, config);
  const values = formValues || {};
  const mode = values.paymentMode || 'free';
  const linkPrice = krInputToOre(values.paymentPrice) || 0;
  const linkFee = krInputToOre(values.paymentBookingFee) || 0;
  const defaultAmount = bookingTotal(
    places,
    Number(event.price) || 0,
    Number(event.bookingFee) || 0
  );

  // The amount paid outside the app follows the places until the admin
  // types their own
  const amountEdited = useRef(false);
  useEffect(() => {
    if (mode === 'external' && !amountEdited.current) {
      change('externalAmount', oreToKrInput(defaultAmount));
    }
  }, [mode, defaultAmount]);

  const deadline =
    values.paymentExpiry !== 'custom'
      ? expiresAtFor(values.paymentExpiry, null, event)
      : null;

  return (
    <div className="invite-modal__full event-payments__participant">
      <span className="form-header">{p('paymentSection')}</span>
      <Field
        name="paymentMode"
        component={ChoiceCards}
        ariaLabel={p('paymentSection')}
        columns={3}
        options={['free', 'link', 'external'].map((key) => ({
          value: key,
          icon: MODE_ICONS[key],
          label: p(`paymentMode-${key}`),
          hint: p(`paymentModeHint-${key}`),
        }))}
      />
      {mode === 'link' && (
        <div className="invite-modal__form mt-3">
          <div className="form__form-group">
            <span className="form-header">
              {p('pricePerPlace', { currency })}
            </span>
            <Field
              name="paymentPrice"
              component={renderField}
              inputMode="decimal"
            />
          </div>
          <div className="form__form-group">
            <span className="form-header">{p('bookingFee', { currency })}</span>
            <Field
              name="paymentBookingFee"
              component={renderField}
              placeholder="0"
              inputMode="decimal"
            />
          </div>
          <div className="form__form-group invite-modal__full">
            <span className="form-header">{p('payBefore')}</span>
            <Field name="paymentExpiry" component={ExpiryField} event={event} />
            {values.paymentExpiry === 'custom' && (
              <div className="mt-2">
                <Field
                  name="paymentExpiresAt"
                  type="datetime-local"
                  component={renderField}
                />
              </div>
            )}
            {deadline && (
              <small className="invite-modal__field-hint">
                {p('payBeforeDate', { date: formatDateTime(deadline) })}
              </small>
            )}
          </div>
          <InfoBox className="invite-modal__full mb-0">
            {email || inApp
              ? p(email ? 'linkSummary' : 'linkSummaryApp', {
                  total: formatMoney(
                    bookingTotal(places, linkPrice, linkFee),
                    currency
                  ),
                  count: places,
                  email,
                })
              : p('linkNeedsEmail')}
          </InfoBox>
        </div>
      )}
      {mode === 'external' && (
        <div className="invite-modal__form mt-3">
          <div className="form__form-group">
            <span className="form-header">{p('externalMethod')}</span>
            <Field
              name="externalMethod"
              component={renderSelectField}
              placeholder={p('externalMethod')}
              options={EXTERNAL_METHODS.map((method) => ({
                value: method,
                label: p(`method-${method}`),
              }))}
            />
          </div>
          <div className="form__form-group">
            <span className="form-header">
              {p('externalAmount', { currency })}
            </span>
            <Field
              name="externalAmount"
              component={renderField}
              inputMode="decimal"
              onChange={() => {
                amountEdited.current = true;
              }}
            />
            <small className="invite-modal__field-hint">
              {p('externalAmountHint', {
                amount: formatMoney(defaultAmount, currency),
                count: places,
              })}
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

ParticipantPaymentFields.propTypes = {
  formValues: PropTypes.shape({}),
  change: PropTypes.func.isRequired,
  event: PropTypes.shape({
    price: PropTypes.number,
    bookingFee: PropTypes.number,
  }).isRequired,
  places: PropTypes.number,
  email: PropTypes.string,
  inApp: PropTypes.bool,
};

ParticipantPaymentFields.defaultProps = {
  formValues: null,
  places: 1,
  email: '',
  inApp: false,
};

export default ParticipantPaymentFields;
